import React, { useState, useEffect, useRef } from "react";
import PageShell, {
  buildDigitalBackpackUrl,
  trackEvent,
} from "../components/PageShell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { API_BASE_URL, DIGITAL_BACKPACK_BASE_URL } from "../config/environment";
import {
  faQrcode,
  faSpinner,
  faCheckCircle,
  faTimes,
  faSignOutAlt,
  faIdCard,
  faTriangleExclamation,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import {
  faUser,
  faCircleCheck as faCircleCheckRegular,
} from "@fortawesome/free-regular-svg-icons";
import { Html5Qrcode } from "html5-qrcode";
import "../styles/app/ConnectionDetailsPage.scss";

const CONNECTION_DATA_KEY = "connectionData";

const ConnectionDetailsPage: React.FC = () => {
  const [notes, setNotes] = useState("");
  const [showSplash, setShowSplash] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const [connectionId, setConnectionId] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: "api" | "qr" | "connectivity" | "general";
  }>({
    show: false,
    title: "",
    message: "",
    type: "general",
  });
  const scannerRef = useRef<HTMLDivElement>(null);
  const qrScannerRef = useRef<Html5Qrcode | null>(null); // Add ref for QR scanner instance
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [userBadge, setUserBadge] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    cdsEventCode: "",
    id: "",
  });
  const [attendee, setAttendee] = useState({
    firstName: "No",
    lastName: "Contact Found",
    title: "",
    company: "",
    email: "",
    phone: "",
    id: "",
    showId: "",
    showCode: "",
  });
  const [exhibitorError, setExhibitorError] = useState(false);
  const [isRecoveryScan, setIsRecoveryScan] = useState(false);
  const isRecoveryScanRef = useRef(false);

  const maxCharacters = 500;
  const orangeThreshold = 400;
  const redThreshold = 450;

  // Position close button at top-right of QR scan region, responsive to screen resize
  useEffect(() => {
    if (!showScanner) return;

    let animFrameId: number;

    const positionButton = () => {
      const button = closeButtonRef.current;
      if (!button) {
        animFrameId = requestAnimationFrame(positionButton);
        return;
      }

      const scanRegion =
        document.getElementById("qr-reader-details__scan_region") ??
        document.querySelector<HTMLElement>('[id$="__scan_region"]') ??
        document.querySelector<HTMLElement>(".qr-shaded-region");

      if (!scanRegion) {
        animFrameId = requestAnimationFrame(positionButton);
        return;
      }

      const rect = scanRegion.getBoundingClientRect();
      if (rect.height <= 0 || rect.width <= 0) {
        animFrameId = requestAnimationFrame(positionButton);
        return;
      }

      button.style.top = `${Math.max(8, Math.round(rect.top) + 8)}px`;
      button.style.right = `${Math.max(8, Math.round(window.innerWidth - rect.right) + 8)}px`;
    };

    animFrameId = requestAnimationFrame(positionButton);
    window.addEventListener("resize", positionButton);

    return () => {
      cancelAnimationFrame(animFrameId);
      window.removeEventListener("resize", positionButton);
    };
  }, [showScanner]);

  // Reset redirect/session states when user navigates back to this page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (isRedirecting || isEndingSession) {
          setIsRedirecting(false);
          setIsEndingSession(false);
          setShowSplash(false);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isRedirecting, isEndingSession]);

  useEffect(() => {
    const splashTimer = setTimeout(() => {
      setShowSplash(false);
    }, 2000);

    const connectionDataStr = localStorage.getItem(CONNECTION_DATA_KEY);
    if (connectionDataStr) {
      try {
        const connectionData = JSON.parse(connectionDataStr);

        if (connectionData && connectionData.id) {
          setConnectionId(connectionData.id);
        }

        if (connectionData && connectionData.notes) {
          setNotes(connectionData.notes);
        }

        if (connectionData && connectionData.scannedBadge) {
          const scannedBadge = connectionData.scannedBadge;
          setAttendee({
            firstName: capitalizeNames(scannedBadge.firstName) || "Unknown",
            lastName: capitalizeNames(scannedBadge.lastName) || "Unknown",
            title: scannedBadge.title || "",
            company: scannedBadge.companyName || "Unknown Company",
            email: scannedBadge.email || "",
            phone: scannedBadge.phone || "",
            id: scannedBadge.id || "",
            showId: scannedBadge.showId || "",
            showCode: scannedBadge.showCode || "",
          });
        }

        if (connectionData && connectionData.userBadge) {
          const userBadgeData = connectionData.userBadge;
          setUserBadge({
            firstName: capitalizeNames(userBadgeData.firstName) || "",
            lastName: capitalizeNames(userBadgeData.lastName) || "",
            companyName: userBadgeData.companyName || "",
            cdsEventCode:
              connectionData.csc ||
              connectionData.cdsEventCode ||
              userBadgeData.cdsEventCode ||
              "",
            id: userBadgeData.id || "",
          });
        }

        // Check if this connection involves an exhibitor
        if (connectionData && connectionData.scannedBadge) {
          checkIfExhibitorConnection(connectionData);
        }
      } catch (error) {
        console.error("Error parsing connection data:", error);
      }
    } else {
      // No connection data found - redirect to home or show error
      console.warn("No connection data found in localStorage");
      // Option 1: Redirect to home page
      // window.location.href = '/';

      // Option 2: Show error modal after splash
      setTimeout(() => {
        showError(
          "No Connection Found",
          "No connection data was found. Please scan a badge to create a new connection.",
          "general",
        );
      }, 2500); // Show after splash disappears
    }

    return () => clearTimeout(splashTimer);
  }, []);

  // Function to check if the connection involves an exhibitor
  const checkIfExhibitorConnection = async (connectionData: any) => {
    try {
      const userBadge = connectionData.userBadge;
      const scannedBadge = connectionData.scannedBadge;

      if (!userBadge || !scannedBadge) return;

      const userShowCode = userBadge.showCode || "";
      const scannedShowCode = scannedBadge.showCode || "";

      const userBadgeUrl = `https://l4e.us/${userShowCode}/${userBadge.id}/${
        userBadge.lastName
      }/${userBadge.firstName}/${userBadge.companyName || "UNKNOWN"}`;
      const scannedBadgeUrl = `https://l4e.us/${scannedShowCode}/${
        scannedBadge.id
      }/${scannedBadge.lastName}/${scannedBadge.firstName}/${
        scannedBadge.companyName || "UNKNOWN"
      }`;

      const apiUrl = `${API_BASE_URL}/connections`;
      const payload = {
        userBadge: userBadgeUrl,
        scannedBadge: scannedBadgeUrl,
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "public, max-age=3600",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();

        // Check if it's an invalid registration type error
        if (response.status === 400 || response.status === 403) {
          try {
            const errorData = JSON.parse(errorText);

            if (
              errorData.code === "INVALID_REG_TYPE" ||
              (errorData.message &&
                (errorData.message
                  .toLowerCase()
                  .includes("invalid registration type") ||
                  errorData.message.toLowerCase().includes("exhibitor") ||
                  errorData.message
                    .toLowerCase()
                    .includes("cannot create connections")))
            ) {
              console.log("Exhibitor connection detected - showing error");
              setExhibitorError(true);
              return;
            }
          } catch (parseError) {
            if (
              errorText.toLowerCase().includes("invalid registration type") ||
              errorText.toLowerCase().includes("exhibitor") ||
              errorText.toLowerCase().includes("cannot create connections") ||
              errorText.includes("INVALID_REG_TYPE")
            ) {
              console.log("Exhibitor connection detected - showing error");
              setExhibitorError(true);
              return;
            }
          }
        }
      }
    } catch (error) {
      console.error("Error checking exhibitor connection:", error);
    }
  };

  const getCounterColor = () => {
    if (notes.length >= redThreshold) {
      return "text-danger";
    } else if (notes.length >= orangeThreshold) {
      return "text-warning";
    }
    return "text-muted";
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= maxCharacters) {
      setNotes(value);
    }
  };

  const showError = (
    title: string,
    message: string,
    type: "api" | "qr" | "connectivity" | "general" = "general",
  ) => {
    setError({
      show: true,
      title,
      message,
      type,
    });
  };

  const closeError = () => {
    setError({
      show: false,
      title: "",
      message: "",
      type: "general",
    });
  };

  const isNetworkError = (error: any) => {
    return (
      !navigator.onLine ||
      error.name === "NetworkError" ||
      error.message?.includes("fetch") ||
      error.message?.includes("network") ||
      error.code === "NETWORK_ERROR"
    );
  };

  // Helper function to capitalize names properly
  const capitalizeNames = (name: string) => {
    if (!name) return name;
    return name
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  function parseBadgeUrl(urlString: string) {
    try {
      const url = new URL(urlString);
      const qrParam = url.searchParams.get("qr");
      let badgeUrl = urlString;
      if (qrParam) {
        badgeUrl = decodeURIComponent(qrParam);
      }

      const badge = new URL(badgeUrl);
      const parts = badge.pathname
        .split("/")
        .filter(Boolean)
        .map((part) => decodeURIComponent(part));

      let badgeId, lastName, firstName, companyName, showCode;
      if (parts.length >= 5) {
        showCode = parts[0];
        badgeId = parts[parts.length - 4];
        lastName = parts[parts.length - 3];
        firstName = parts[parts.length - 2];
        companyName = parts.slice(parts.length - 1)[0];
      } else {
        throw new Error("QR code format invalid");
      }

      return {
        showCode,
        badgeId,
        lastName,
        firstName,
        companyName,
        raw: badgeUrl,
      };
    } catch (e) {
      return null;
    }
  }

  // Helper function to check if user is an exhibitor
  const checkRegistrationType = (userData: any) => {
    // Check various possible fields for registration type
    const registrationType =
      userData.registrationType ||
      userData.registration_type ||
      userData.type ||
      userData.userType ||
      userData.user_type ||
      userData.badgeType ||
      userData.badge_type;

    if (!registrationType) {
      console.warn("No registration type found in user data");
      return false; // Default to allow if no type found
    }

    const typeStr = registrationType.toString().toLowerCase();

    // Check for exhibitor patterns
    return (
      typeStr.includes("exhibitor") ||
      typeStr.includes("exhibit") ||
      typeStr === "ex" ||
      typeStr === "e"
    );
  };

  const showExhibitorError = () => {
    showError(
      "Exhibitor Badge Detected",
      "Attendee Connect only works with attendee badges.\n\nWhat to do next:\n1. Ask the exhibitor to scan your badge with their lead retrieval device.\n2. Their contact details will appear in your Exhibit Tracker section.",
      "qr",
    );
  };

  const startScanner = () => {
    setShowScanner(true);
    setPermissionDenied(false);

    // Immediate camera permission check for Safari compatibility
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setPermissionDenied(true);
      setShowScanner(false);
      return;
    }

    // Check for HTTPS requirement (Safari requirement)
    if (
      window.location.protocol !== "https:" &&
      window.location.hostname !== "localhost"
    ) {
      setPermissionDenied(true);
      setShowScanner(false);
      return;
    }

    // Add small delay to ensure DOM element is rendered
    setTimeout(() => {
      if (scannerRef.current) {
        const html5QrCode = new Html5Qrcode("qr-reader-details");
        qrScannerRef.current = html5QrCode; // Store the scanner instance
        html5QrCode
          .start(
            { facingMode: "environment" },
            { fps: 10, qrbox: 250 },
            async (decodedText) => {
              const badgeObj = parseBadgeUrl(decodedText);
              if (!badgeObj) {
                html5QrCode.stop();
                qrScannerRef.current = null; // Clear the ref after stopping
                setShowScanner(false);
                showError(
                  "Invalid QR Code",
                  "The QR code you scanned is not a valid badge QR code. Please try scanning a different badge.",
                  "qr",
                );
                return;
              }

              html5QrCode.stop();
              qrScannerRef.current = null; // Clear the ref after stopping
              setShowScanner(false);
              setIsProcessing(true);
              setShowSplash(true);

              const connectionDataStr =
                localStorage.getItem(CONNECTION_DATA_KEY);
              let userBadgeInfo = null;

              try {
                const connectionData = connectionDataStr
                  ? JSON.parse(connectionDataStr)
                  : {};
                userBadgeInfo = connectionData.userBadge;
              } catch (error) {
                console.error("Error parsing connection data:", error);
              }

              if (isRecoveryScanRef.current) {
                // Recovery mode: store scanned badge as user's own badge
                const userBadgeData = {
                  id: badgeObj.badgeId,
                  firstName: badgeObj.firstName,
                  lastName: badgeObj.lastName,
                  companyName: badgeObj.companyName,
                  showCode: badgeObj.showCode,
                };

                const recoveryDataStr =
                  localStorage.getItem(CONNECTION_DATA_KEY);
                let recoveryData: Record<string, unknown> = {};
                if (recoveryDataStr) {
                  try {
                    recoveryData = JSON.parse(recoveryDataStr);
                  } catch (e) {}
                }
                recoveryData.userBadge = userBadgeData;
                localStorage.setItem(
                  CONNECTION_DATA_KEY,
                  JSON.stringify(recoveryData),
                );

                // Update React state
                setUserBadge({
                  firstName: capitalizeNames(badgeObj.firstName) || "",
                  lastName: capitalizeNames(badgeObj.lastName) || "",
                  companyName: badgeObj.companyName || "",
                  cdsEventCode:
                    (recoveryData.csc as string) ||
                    (recoveryData.cdsEventCode as string) ||
                    badgeObj.showCode ||
                    "",
                  id: badgeObj.badgeId || "",
                });

                isRecoveryScanRef.current = false;
                setIsRecoveryScan(false);
                setIsProcessing(false);
                setShowSplash(false);
                return; // Skip the connection API call
              }

              if (!userBadgeInfo && !isRecoveryScanRef.current) {
                setIsProcessing(false);
                setShowSplash(false);
                showError(
                  "User Badge Missing",
                  "User badge information not found. Please scan your badge first.",
                  "general",
                );
                return;
              }

              try {
                const apiUrl = `${API_BASE_URL}/connections`;
                const userShowCode = userBadgeInfo.showCode || "";
                const scannedShowCode = badgeObj.showCode || "";

                const userBadgeUrl = `https://l4e.us/${userShowCode}/${
                  userBadgeInfo.id
                }/${userBadgeInfo.lastName}/${userBadgeInfo.firstName}/${
                  userBadgeInfo.companyName || "UNKNOWN"
                }`;
                const scannedBadgeUrl = `https://l4e.us/${scannedShowCode}/${
                  badgeObj.badgeId
                }/${badgeObj.lastName}/${badgeObj.firstName}/${
                  badgeObj.companyName || "UNKNOWN"
                }`;

                const payload = {
                  userBadge: userBadgeUrl,
                  scannedBadge: scannedBadgeUrl,
                };

                const response = await fetch(apiUrl, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "Cache-Control": "public, max-age=3600",
                  },
                  body: JSON.stringify(payload),
                });

                if (!response.ok) {
                  const errorText = await response.text();

                  // Check if it's an invalid registration type error
                  if (response.status === 400 || response.status === 403) {
                    try {
                      const errorData = JSON.parse(errorText);

                      // Check for specific INVALID_REG_TYPE code
                      if (errorData.code === "INVALID_REG_TYPE") {
                        console.log(
                          "Invalid registration type code detected - navigating to landing page with exhibitor info",
                        );
                        setIsProcessing(false);
                        setShowSplash(false);

                        // Navigate to landing page with exhibitor info (no error flag yet)
                        const params = new URLSearchParams({
                          showId: badgeObj.showCode || "",
                          badgeId: badgeObj.badgeId || "",
                          firstName: badgeObj.firstName || "",
                          lastName: badgeObj.lastName || "",
                          companyName: (badgeObj.companyName || "").replace(
                            / /g,
                            "-",
                          ),
                        });

                        window.location.href = `/?${params.toString()}`;
                        return;
                      }

                      // Check for message-based detection
                      if (
                        errorData.message &&
                        (errorData.message
                          .toLowerCase()
                          .includes("invalid registration type") ||
                          errorData.message
                            .toLowerCase()
                            .includes("exhibitor") ||
                          errorData.message
                            .toLowerCase()
                            .includes("cannot create connections") ||
                          errorData.error
                            ?.toLowerCase()
                            .includes("invalid registration type") ||
                          errorData.error?.toLowerCase().includes("exhibitor"))
                      ) {
                        console.log(
                          "Invalid registration type detected - navigating to landing page with error",
                        );
                        setIsProcessing(false);
                        setShowSplash(false);

                        // Navigate to landing page with exhibitor error state using scanned badge info
                        const params = new URLSearchParams({
                          showId: badgeObj.showCode || "",
                          badgeId: badgeObj.badgeId || "",
                          firstName: badgeObj.firstName || "",
                          lastName: badgeObj.lastName || "",
                          companyName: (badgeObj.companyName || "").replace(
                            / /g,
                            "-",
                          ),
                          exhibitorError: "true",
                        });

                        window.location.href = `/?${params.toString()}`;
                        return;
                      }
                    } catch (parseError) {
                      // If JSON parsing fails, check plain text error message
                      if (
                        errorText
                          .toLowerCase()
                          .includes("invalid registration type") ||
                        errorText.toLowerCase().includes("exhibitor") ||
                        errorText
                          .toLowerCase()
                          .includes("cannot create connections") ||
                        errorText.includes("INVALID_REG_TYPE")
                      ) {
                        console.log(
                          "Invalid registration type detected in error text - navigating to landing page with error",
                        );
                        setIsProcessing(false);
                        setShowSplash(false);

                        // Navigate to landing page with exhibitor error state using scanned badge info
                        const params = new URLSearchParams({
                          showId: badgeObj.showCode || "",
                          badgeId: badgeObj.badgeId || "",
                          firstName: badgeObj.firstName || "",
                          lastName: badgeObj.lastName || "",
                          companyName: (badgeObj.companyName || "").replace(
                            / /g,
                            "-",
                          ),
                          exhibitorError: "true",
                        });

                        window.location.href = `/?${params.toString()}`;
                        return;
                      }
                    }
                  }

                  throw new Error(
                    `API request failed: ${response.status} - ${errorText}`,
                  );
                }

                const data = await response.json();

                // Check for invalid registration type in success response as well
                if (
                  (data.error &&
                    (data.error
                      .toLowerCase()
                      .includes("invalid registration type") ||
                      data.error.toLowerCase().includes("exhibitor"))) ||
                  data.code === "INVALID_REG_TYPE"
                ) {
                  console.log(
                    "Invalid registration type in response data - navigating to landing page with error",
                  );
                  setIsProcessing(false);
                  setShowSplash(false);

                  // Navigate to landing page with exhibitor error state using scanned badge info
                  const params = new URLSearchParams({
                    showId: badgeObj.showCode || "",
                    badgeId: badgeObj.badgeId || "",
                    firstName: badgeObj.firstName || "",
                    lastName: badgeObj.lastName || "",
                    companyName: (badgeObj.companyName || "").replace(
                      / /g,
                      "-",
                    ),
                    exhibitorError: "true",
                  });

                  window.location.href = `/?${params.toString()}`;
                  return;
                }

                // Validate user badge registration type
                if (
                  data.data?.userBadge &&
                  checkRegistrationType(data.data.userBadge)
                ) {
                  console.log("Exhibitor detected - showing error popup");
                  setIsProcessing(false);
                  setShowSplash(false);
                  showExhibitorError();
                  return;
                }

                // Also validate scanned badge registration type
                if (
                  data.data?.scannedBadge &&
                  checkRegistrationType(data.data.scannedBadge)
                ) {
                  console.log(
                    "Scanned badge is exhibitor - navigating to landing page with error",
                  );
                  setIsProcessing(false);
                  setShowSplash(false);

                  // Navigate to landing page with exhibitor error state
                  const scannedBadge = data.data.scannedBadge;
                  const params = new URLSearchParams({
                    badgeId: scannedBadge.id || "",
                    firstName: scannedBadge.firstName || "",
                    lastName: scannedBadge.lastName || "",
                    companyName: (scannedBadge.companyName || "").replace(
                      / /g,
                      "-",
                    ),
                    exhibitorError: "true",
                  });

                  window.location.href = `/?${params.toString()}`;
                  return;
                }

                setAttendee({
                  firstName: capitalizeNames(badgeObj.firstName) || "Unknown",
                  lastName: capitalizeNames(badgeObj.lastName) || "Unknown",
                  title: "",
                  company: badgeObj.companyName || "Unknown Company",
                  email: data.data?.scannedBadge?.email || "",
                  phone: "",
                  id: badgeObj.badgeId || "",
                  showId: "",
                  showCode: "",
                });

                if (data.data?.id) {
                  setConnectionId(data.data.id);
                }
                if (data.data?.notes) {
                  setNotes(data.data.notes);
                } else {
                  setNotes("");
                }

                // Merge showCode per badge since API may not return it
                const dataToStore = { ...data.data };
                if (dataToStore.userBadge && !dataToStore.userBadge.showCode) {
                  // Fall back to scanned badge's showCode (e.g. user was manual entry)
                  dataToStore.userBadge = {
                    ...dataToStore.userBadge,
                    showCode: userShowCode || scannedShowCode,
                  };
                }
                if (
                  dataToStore.scannedBadge &&
                  !dataToStore.scannedBadge.showCode
                ) {
                  dataToStore.scannedBadge = {
                    ...dataToStore.scannedBadge,
                    showCode: scannedShowCode,
                  };
                }
                localStorage.setItem(
                  CONNECTION_DATA_KEY,
                  JSON.stringify(dataToStore),
                );

                setIsProcessing(false);
                setTimeout(() => {
                  setShowSplash(false);
                }, 1500);
              } catch (error) {
                console.error("Error making connection:", error);
                setIsProcessing(false);
                setShowSplash(false);

                if (isNetworkError(error)) {
                  showError(
                    "Connection Problem",
                    "Please check your internet connection and try again.",
                    "connectivity",
                  );
                } else {
                  showError(
                    "Connection Failed",
                    "Failed to save connection. Please try again.",
                    "api",
                  );
                }
              }
            },
            (errorMessage) => {
              // REMOVED: Don't track QR scan errors (fires 10 times per second)
              // These are normal scanning errors when no valid QR code is in frame
            },
          )
          .catch((err) => {
            console.error("Camera start error:", err);
            setPermissionDenied(true);
            setShowScanner(false);

            // Additional Safari-specific error handling
            if (
              err.name === "NotAllowedError" ||
              err.name === "PermissionDeniedError" ||
              err.message?.includes("Permission denied") ||
              err.message?.includes("NotAllowed")
            ) {
              setPermissionDenied(true);
            }
          });
      }
    }, 100);
  };

  const closeScanner = () => {
    // Stop the camera if it's running
    if (qrScannerRef.current) {
      qrScannerRef.current
        .stop()
        .then(() => {
          console.log("Camera stopped successfully");
        })
        .catch((err) => {
          console.error("Error stopping camera:", err);
        })
        .finally(() => {
          qrScannerRef.current = null; // Clear the ref
        });
    }

    setShowScanner(false);
    setPermissionDenied(false);
  };

  const resolveEventCode = () => {
    const connectionDataStr = localStorage.getItem(CONNECTION_DATA_KEY);
    let eventCode = userBadge.cdsEventCode;

    if (connectionDataStr) {
      try {
        const connectionData = JSON.parse(connectionDataStr);
        eventCode =
          connectionData.csc ||
          connectionData.cdsEventCode ||
          userBadge.cdsEventCode;
      } catch (error) {
        console.error("Error parsing connection data for event code:", error);
      }
    }

    return eventCode;
  };

  const handleEndSession = () => {
    setShowEndSessionModal(true);
    setShowUserDropdown(false);
  };

  const confirmEndSession = async () => {
    setIsEndingSession(true);
    setShowSplash(true);
    setShowEndSessionModal(false);

    const eventCode = resolveEventCode();

    localStorage.removeItem(CONNECTION_DATA_KEY);
    localStorage.removeItem("attendeeBadgeInfo");

    // Build URL with UTM parameters and SSO params
    const digitalBackpackUrl = buildDigitalBackpackUrl(
      DIGITAL_BACKPACK_BASE_URL,
      eventCode,
      userBadge.id,
      userBadge.lastName,
    );

    window.location.href = digitalBackpackUrl;
  };

  const cancelEndSession = () => {
    setShowEndSessionModal(false);
  };

  const handleDigitalBackpack = () => {
    // Track Digital Backpack button click
    trackEvent("digital_backpack_click", {
      page_path: window.location.pathname,
      event_code: resolveEventCode(),
    });

    const eventCode = resolveEventCode();

    if (!eventCode) {
      showError(
        "Digital Backpack Access Error",
        "Unable to access Digital Backpack. Missing event information.",
        "general",
      );
      return;
    }

    // Build SSO URL with badge ID and last name for auto-login
    const digitalBackpackUrl = buildDigitalBackpackUrl(
      DIGITAL_BACKPACK_BASE_URL,
      eventCode,
      userBadge.id,
      userBadge.lastName,
    );

    // Open in new tab; fall back to same-tab navigation if popup is blocked
    const newTab = window.open(digitalBackpackUrl, "_blank");
    if (!newTab) {
      window.location.href = digitalBackpackUrl;
    }
  };

  // Add useEffect for click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserDropdown]);

  const safeMerge = (
    existing: Record<string, unknown>,
    incoming: Record<string, unknown>,
  ): Record<string, unknown> => {
    const filtered: Record<string, unknown> = {};
    for (const key of Object.keys(incoming)) {
      if (incoming[key] !== undefined) {
        filtered[key] = incoming[key];
      }
    }
    return { ...existing, ...filtered };
  };

  const handleSaveNotes = async () => {
    const eventCode = resolveEventCode();

    if (!connectionId || !eventCode || notes.trim().length === 0) {
      console.error("Missing required data for saving notes");
      return;
    }

    setIsSaving(true);

    try {
      const apiUrl = `${API_BASE_URL}/connections/${eventCode}/${connectionId}`;

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "public, max-age=3600",
        },
        body: JSON.stringify({
          notes: notes.trim(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `API request failed: ${response.status} - ${errorText}`,
        );
      }

      const data = await response.json();

      // Check for exhibitor type in response (in case registration type changed)
      if (data.data?.userBadge && checkRegistrationType(data.data.userBadge)) {
        console.log(
          "Exhibitor detected during note save - showing error popup",
        );
        showExhibitorError();
        return;
      }

      // Merge API response into existing localStorage data to preserve
      // fields not returned by the PUT endpoint (e.g., userBadge)
      if (data.data) {
        let existingData: Record<string, unknown> = {};
        const currentDataStr = localStorage.getItem(CONNECTION_DATA_KEY);
        if (currentDataStr) {
          try {
            existingData = JSON.parse(currentDataStr);
          } catch (e) {
            // If parse fails, fall back to empty object
          }
        }
        const merged = safeMerge(
          existingData,
          data.data as Record<string, unknown>,
        );
        localStorage.setItem(CONNECTION_DATA_KEY, JSON.stringify(merged));
      }

      console.log("Notes saved successfully:", data);
    } catch (error) {
      console.error("Error saving notes:", error);
      alert("Failed to save notes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (showSplash) {
    return (
      <div className="cdp-splash-overlay">
        <div className="cdp-splash-content">
          <FontAwesomeIcon
            icon={
              isProcessing || isRedirecting || isEndingSession
                ? faSpinner
                : faCheckCircle
            }
            spin={isProcessing || isRedirecting || isEndingSession}
            className={`cdp-splash-checkmark ${
              isProcessing || isRedirecting || isEndingSession
                ? "cdp-spinner"
                : ""
            }`}
          />
          <div className="cdp-splash-text">
            {isEndingSession
              ? "Ending session..."
              : isRedirecting
                ? "Redirecting to Digital Backpack..."
                : isProcessing
                  ? "Connecting..."
                  : "Connection Saved!"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageShell>
      {/* User info header */}
      <div className="cdp-user-header">
        <div className="container d-flex justify-content-end align-items-center py-1">
          <div
            className="cdp-user-dropdown position-relative"
            ref={dropdownRef}
          >
            <button
              className="btn btn-link cdp-user-button"
              onClick={() => setShowUserDropdown(!showUserDropdown)}
            >
              <FontAwesomeIcon icon={faUser} className="cdp-user-icon" />
            </button>
            {showUserDropdown && (
              <div className="cdp-dropdown-menu">
                <div className="cdp-dropdown-header">
                  <div className="cdp-dropdown-user">
                    <FontAwesomeIcon
                      icon={faUser}
                      className="cdp-dropdown-user-icon"
                    />
                    <div className="cdp-user-name-full">
                      {userBadge.firstName && userBadge.lastName
                        ? `${userBadge.firstName} ${userBadge.lastName}`
                        : "Guest User"}
                    </div>
                  </div>
                  <button
                    className="cdp-close-button"
                    onClick={() => setShowUserDropdown(false)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                <hr className="cdp-dropdown-divider" />

                <div className="cdp-company-section">
                  <div className="cdp-company-label">Company</div>
                  <div className="cdp-company-name">
                    {userBadge.companyName &&
                    userBadge.companyName !== "UNKNOWN"
                      ? userBadge.companyName
                      : ""}
                  </div>
                </div>

                <hr className="cdp-dropdown-divider" />

                <button
                  className="cdp-logout-button"
                  onClick={handleEndSession}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* End Session Confirmation Modal */}
      {showEndSessionModal && (
        <div className="cdp-modal-overlay">
          <div className="cdp-modal-content">
            <h5 className="mb-3">End Session</h5>
            <p className="mb-4">
              Are you sure you want to end your session and log out? <br />
              <br />
            </p>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-secondary" onClick={cancelEndSession}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={confirmEndSession}>
                Yes, End Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {error.show && (
        <div className="cdp-modal-overlay">
          <div className="cdp-modal-content">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">{error.title}</h5>
              <button
                className="btn-close"
                aria-label="Close"
                onClick={closeError}
              />
            </div>
            <p className="mb-4">{error.message}</p>
            {error.title === "User Badge Missing" ? (
              <div className="d-flex justify-content-end gap-2">
                <button className="btn btn-secondary" onClick={closeError}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    isRecoveryScanRef.current = true;
                    setIsRecoveryScan(true);
                    closeError();
                    startScanner();
                  }}
                >
                  Scan Your Own Badge
                </button>
              </div>
            ) : (
              <div className="d-flex justify-content-end">
                <button className="btn btn-primary" onClick={closeError}>
                  OK
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <header className=" bg-primary text-white text-center py-3">
        <span className="cdp-header-title h2 fw-semibold">
          Connection Details
        </span>
        <div className="cdp-card-container d-flex justify-content-center align-items-center mt-4">
          <div className="cdp-card card shadow-sm">
            <div className="card-body text-center">
              {exhibitorError ? (
                // Show exhibitor error — new panel style
                <section className="as-exhibitor-panel" aria-label="Exhibitor badge scanned">
                  <div className="as-exhibitor-icon-wrap" aria-hidden="true">
                    <div className="as-exhibitor-icon-main">
                      <FontAwesomeIcon icon={faIdCard} />
                    </div>
                    <div className="as-exhibitor-icon-badge">
                      <FontAwesomeIcon icon={faTriangleExclamation} />
                    </div>
                  </div>
                  <h2 className="as-exhibitor-title">Exhibitor Badge Scanned</h2>
                  <p className="as-exhibitor-message">
                    To connect, have the exhibitor scan your badge with their lead retrieval device.
                  </p>
                  <div className="as-exhibitor-info-card" role="note">
                    <FontAwesomeIcon icon={faCircleInfo} className="as-exhibitor-info-icon" />
                    <span>
                      Details will appear in Exhibit Tracker section in Digital Backpack.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="as-exhibitor-scan-btn"
                    onClick={startScanner}
                  >
                    <FontAwesomeIcon icon={faQrcode} className="me-2" />
                    Scan Another Badge
                  </button>
                  <button
                    type="button"
                    className="as-exhibitor-tracker-link"
                    onClick={handleDigitalBackpack}
                  >
                    Go to Digital Backpack
                  </button>
                </section>
              ) : (
                // Show normal success message
                <>
                  <div className="cdp-success-section bg-white text-center py-0">
                    <div className="cdp-connection-success mb-4">
                      <div>
                        <FontAwesomeIcon
                          icon={faCircleCheckRegular}
                          size="3x"
                          className="cdp-success-icon"
                        />
                      </div>
                      <div className="cdp-connection-msg fw-semibold mb-4 text-center">
                        You've made a new connection!
                      </div>
                    </div>
                  </div>
                  <div className="cdp-attendee-name fw-semibold mb-2 h5">
                    {attendee.firstName} {attendee.lastName}
                  </div>
                  {attendee.title && (
                    <div className="cdp-attendee-title fw-bold text-secondary mb-2">
                      {attendee.title}
                    </div>
                  )}
                  <div className="cdp-attendee-company text-muted mb-2">
                    {attendee.company}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
      <main className="cdp-main container d-flex flex-column align-items-center bg-light min-vh-75 py-4">
        <div className="cdp-notes-section">
          <div className="cdp-notes-label mb-3 fw-semibold">Notes</div>
          <div className="cdp-notes-wrapper w-100">
            <textarea
              className="cdp-notes form-control"
              rows={4}
              placeholder="Add notes about this connection..."
              value={notes}
              onChange={handleNotesChange}
              maxLength={maxCharacters}
            />
            <div className="cdp-notes-footer d-flex justify-content-between align-items-center mt-2">
              <div className={`cdp-character-counter ${getCounterColor()}`}>
                {notes.length}/{maxCharacters}
              </div>
              <button
                className="btn btn-primary cdp-save-btn"
                type="button"
                disabled={notes.trim().length === 0 || isSaving}
                onClick={handleSaveNotes}
              >
                {isSaving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="cdp-action-buttons">
          <button className="cdp-scan-btn btn" onClick={startScanner}>
            <FontAwesomeIcon icon={faQrcode} className="me-2" />
            Scan Another Badge
          </button>
          <button
            className="cdp-backpack-btn btn btn-outline-primary"
            onClick={handleDigitalBackpack}
          >
            View My Connections
          </button>
        </div>
      </main>

      {/* Scanner overlay */}
      {showScanner && (
        <div className="cdp-scanner-overlay">
          <div className="cdp-scanner-cam-wrapper">
            <div
              className="cdp-scanner-cam"
              id="qr-reader-details"
              ref={scannerRef}
            ></div>
          </div>
          <button
            ref={closeButtonRef}
            className="cdp-scanner-close"
            onClick={closeScanner}
            aria-label="Close scanner"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {permissionDenied && showScanner && (
        <div className="alert alert-danger cdp-scanner-error">
          Camera permission denied. Please allow camera access to scan badges.
        </div>
      )}
    </PageShell>
  );
};

export default ConnectionDetailsPage;
